import { InjectionToken } from '@angular/core';

/**
 * App-level configuration for `@sisoma/media`, populated per surface by
 * `provideMedia()` (see `./provide-media`). Lives in the
 * `@sisoma/media/providers` entry point with it, so the library's services and
 * an app's `app.config.ts` share this one token — see `../public-api.ts`.
 */
export interface MediaConfig {
  /**
   * Base URL of the API. Every call the library makes that is *ours* is built
   * from this — the Cloudinary signature endpoint, the user media library, and
   * the stock-provider proxy. The default is empty, which leaves those calls
   * same-origin and relative until an app configures it.
   *
   * Note this is the same value an app passes to `provideAuth({ apiUrl })`, and
   * deliberately so: `authInterceptor` only attaches credentials to requests
   * under *its* `apiUrl`, so a mismatch here would silently strip the cookie and
   * the active-partner header off every media call. The direct-to-Cloudinary
   * upload is the one request that is intentionally outside this base.
   */
  apiUrl: string;
}

/**
 * What {@link MEDIA_CONFIG} holds until an app configures it. Internal to this
 * entry point: `provideMedia()` merges over it and the token's factory
 * falls back to it, and nothing outside needs the value itself.
 */
export const MEDIA_DEFAULTS: MediaConfig = {
  apiUrl: '',
};

/**
 * Injection token for {@link MediaConfig}. Defaults to {@link MEDIA_DEFAULTS} so
 * the library works unconfigured (and in tests); `provideMedia()` overrides it
 * per app.
 */
export const MEDIA_CONFIG = new InjectionToken<MediaConfig>('MEDIA_CONFIG', {
  providedIn: 'root',
  factory: () => MEDIA_DEFAULTS,
});
